import isNil from 'lodash/isNil';
import {
    Button,
    CloseButtonViewOuter,
    InputChangeEvent,
    Modal,
    ModalCloseProps,
    H3,
    SimpleCallback,
    SimpleText,
    Text,
    useIntl,
} from 'platform-components';
import { UI_KIT_HEADER_TYPOGRAPHY, UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components/constants';
import { isValidHumanName, isValidSNLSNumber } from 'platform-components/utils';
import React, { useEffect, useState } from 'react';

import { splitFullName } from 'common.components/certificate-modal/utils';
import { CERTIFICATES_EXPANDED_PARAMS_MODAL_CONFIG } from 'common.components/certificates-expanded-params-modal/config';
import {
    CertGenExpandedParams,
    CERTIFICATES_EXPANDED_FORM_FIELD_INFO_TYPE,
    CertificatesExpandedParamsModalData,
    CertificatesExpandedParamsModalFieldConfig,
} from 'common.components/certificates-expanded-params-modal/typings';

import CertificatesExpandedParamsModalFormField from './__form-field/certificates-expanded-params-modal__form-field';

import './certificates-expanded-params-modal.scss';

export type CertificatesParamsModalData = {
    [key in CERTIFICATES_EXPANDED_FORM_FIELD_INFO_TYPE]: any;
};

interface Props {
    certificatesData: CertificatesExpandedParamsModalData;
    closeModal: SimpleCallback;
    onApprove: (params: CertGenExpandedParams) => void;
    progress: boolean;
}

const CertificatesExpandedParamsModal = (props: Props) => {
    const { certificatesData, closeModal, onApprove, progress } = props;

    const intl = useIntl();

    const [formData, setFormData] = useState(certificatesData);
    const [errorMessages, setErrorMessages] = useState<
        Record<CERTIFICATES_EXPANDED_FORM_FIELD_INFO_TYPE, string | null>
    >({
        need_paper_variant: null,
        citizenship_others: null,
        citizenship_ru: null,
        date_of_birth: null,
        fullName: null,
        gender: null,
        has_higher_education: null,
        locality: null,
        residence_address: null,
        school: null,
        snls: null,
    });

    const configRequiredInfoTypes = CERTIFICATES_EXPANDED_PARAMS_MODAL_CONFIG.filter(
        (formFieldConfig) => formFieldConfig.required
    ).map((formFieldConfig) => formFieldConfig.infoType);

    useEffect(() => {
        setFormData(certificatesData);
    }, [certificatesData]);

    const handleChangeValue = (
        { target: { value } }: InputChangeEvent,
        { infoType }: CertificatesExpandedParamsModalFieldConfig
    ) => {
        setFormData((prevFormData) => ({
            ...prevFormData,
            [infoType]: value,
        }));
        setErrorMessages((prevErrorMessages) => ({
            ...prevErrorMessages,
            [infoType]: null,
        }));
    };

    // eslint-disable-next-line complexity
    const handleValidate = (formFieldConfig: CertificatesExpandedParamsModalFieldConfig) => {
        let errorMessageId: string | null = null;

        const { infoType, required } = formFieldConfig;
        const currentValue = formData[infoType];

        if (required && (isNil(currentValue) || currentValue === '')) {
            errorMessageId = `certificatesExpandedParamsModal.errorMessage.${infoType}.required`;
        } else if (
            infoType === CERTIFICATES_EXPANDED_FORM_FIELD_INFO_TYPE.FULL_NAME &&
            !isValidHumanName(formData.fullName)
        ) {
            errorMessageId = 'certificatesExpandedParamsModal.errorMessage.fullName.invalid';
        } else if (infoType === CERTIFICATES_EXPANDED_FORM_FIELD_INFO_TYPE.SNLS && !isValidSNLSNumber(formData.snls)) {
            errorMessageId = 'certificatesExpandedParamsModal.errorMessage.snls.invalid';
        }

        const errorMessageStr = errorMessageId ? intl.formatMessage({ id: errorMessageId }) : null;

        setErrorMessages((prevErrorMessages) => ({
            ...prevErrorMessages,
            [infoType]: errorMessageStr,
        }));

        return !errorMessages[infoType];
    };

    const getIsFormValid = () => {
        let isValid = true;
        let key: keyof CertificatesExpandedParamsModalData;

        for (key in formData) {
            if (
                (!formData[key] &&
                    configRequiredInfoTypes.includes(key as CERTIFICATES_EXPANDED_FORM_FIELD_INFO_TYPE)) ||
                errorMessages[key]
            ) {
                isValid = false;
            }
        }

        return isValid;
    };

    const handleApprove = () => {
        const { fullName, school } = formData;
        const { first_name = '', last_name = '', middle_name = '' } = splitFullName(fullName);
        const now = new Date().toISOString();

        onApprove({ first_name, last_name, middle_name, school_name: school, date_updated: now, ...formData });
        closeModal();
    };

    const handleClose = () => {
        closeModal();
    };

    return (
        <Modal
            Close={(closeProps: ModalCloseProps) => (
                <CloseButtonViewOuter {...closeProps} position={CloseButtonViewOuter.POSITION.ABSOLUTE} />
            )}
            hasClose
            mix="certificates-expanded-params-modal"
            onRequestClose={handleClose}
            opened={true}
            theme={Modal.THEMES.TEACHER}
        >
            <Modal.Header>
                <H3 id="certificatesExpandedParamsModal.title" typography={UI_KIT_HEADER_TYPOGRAPHY.headlineMMedium} />
            </Modal.Header>
            <Modal.Content>
                <Text
                    className="certificates-expanded-params-modal__description"
                    id="certificatesExpandedParamsModal.description"
                    type={Text.TYPE.MARKDOWN}
                    typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphXLRegular}
                />
                {CERTIFICATES_EXPANDED_PARAMS_MODAL_CONFIG.map((formFieldConfig) => {
                    const { infoType } = formFieldConfig;

                    return (
                        <CertificatesExpandedParamsModalFormField
                            config={formFieldConfig}
                            errorMessage={errorMessages[infoType]}
                            key={`certificates-expanded-params-${infoType}-form-field`}
                            onBlur={() => handleValidate(formFieldConfig)}
                            onChangeValue={(e) => handleChangeValue(e, formFieldConfig)}
                            value={formData[infoType]}
                        />
                    );
                })}
            </Modal.Content>
            <Modal.Footer>
                <Button onClick={handleClose} view={Button.VIEW.SECONDARY}>
                    <SimpleText id="certificatesParamsModal.cancelButtonText" />
                </Button>
                <Button
                    disabled={!getIsFormValid()}
                    onClick={handleApprove}
                    progress={progress}
                    view={Button.VIEW.ACTION}
                >
                    <SimpleText id="certificatesParamsModal.approveButtonText" />
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CertificatesExpandedParamsModal;
